import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	getTutorials,
	getTutorialsRemaining,
	updateTutorialsRemaining,
} from '@/services/tutorials';
import type {
	UpdateTutorialsRemainingPayload,
	UpdateTutorialsRemainingResponse,
} from '@/types/tutorials';

/**
 * Hook to get tutorials remaining for a user
 * @param userId - The user ID (required)
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query hook for tutorials remaining
 */
export const useGetTutorialsRemaining = (
	userId: string | null,
	enabled = true,
) => {
	return useQuery({
		queryKey: ['tutorialsRemaining', userId],
		queryFn: () => {
			if (!userId) {
				throw new Error('userId is required');
			}
			return getTutorialsRemaining(userId);
		},
		enabled: enabled && !!userId,
		keepPreviousData: false,
	});
};

/**
 * Hook to update tutorials remaining for a user
 * @param onSuccess - Callback when update succeeds
 * @param onError - Callback when update fails
 * @returns React Query mutation hook for updating tutorials remaining
 */
export const useUpdateTutorialsRemaining = (
	onSuccess?: (data: UpdateTutorialsRemainingResponse['data']) => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: UpdateTutorialsRemainingPayload) =>
			updateTutorialsRemaining(payload),
		onSuccess: (data) => {
			// Invalidate and refetch tutorials remaining for the updated user
			queryClient.invalidateQueries(['tutorialsRemaining', data.userId]);
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			onError?.(error);
		},
	});
};

export const useGetTutorials = (enabled = true) => {
	return useQuery({
		queryKey: ['tutorials'],
		queryFn: () => getTutorials(),
		enabled,
		keepPreviousData: true,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};
